import React, { useState, useEffect } from 'react'
import { DollarSign, TrendingUp, Users, FileText, Target, Settings, ArrowRight } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

interface AttributionData {
  totalRevenue: number
  trustCenterInfluenced: number
  conversionRate: number
  avgDealSize: number
  topDocuments: {
    name: string
    downloads: number
    dealsInfluenced: number
    revenue: number
  }[]
  timeline: {
    month: string
    trustCenterLeads: number
    closedDeals: number
    revenue: number
  }[]
}

interface RevenueAttributionPageProps {
  embedded?: boolean
}

const RevenueAttributionPage: React.FC<RevenueAttributionPageProps> = ({ embedded = false }) => {
  const { currentTenant } = useTenant()
  const [data, setData] = useState<AttributionData | null>(null)
  const [loading, setLoading] = useState(true)
  const [crmConnected, setCrmConnected] = useState(false)

  useEffect(() => {
    if (currentTenant) {
      // Check if CRM is connected
      // In production, this would check actual CRM connection status
      setCrmConnected(false)
      setLoading(false)
    }
  }, [currentTenant])

  if (loading) {
    return (
      <div className={embedded ? "p-8" : "min-h-screen bg-gray-50 flex items-center justify-center"}>
        <div className="text-center">
          <DollarSign className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading revenue data...</p>
        </div>
      </div>
    )
  }

  // Show setup prompt if CRM not connected
  if (!crmConnected) {
    const setupContent = (
      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="bg-blue-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <DollarSign className="h-12 w-12 text-blue-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">Connect Your CRM to Track Revenue Attribution</h2>

            <p className="text-lg text-gray-600 mb-8">
              Revenue Attribution helps you understand how your trust center influences deals and drives revenue.
              Once connected, you'll see:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <TrendingUp className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Revenue Metrics</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Total revenue influenced by trust center</li>
                  <li>• Conversion rate of prospects to customers</li>
                  <li>• Average deal size</li>
                  <li>• Monthly performance trends</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-3">
                  <FileText className="h-6 w-6 text-purple-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Document Impact</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Which documents drive the most deals</li>
                  <li>• Download-to-deal conversion rates</li>
                  <li>• Revenue attributed to each document</li>
                  <li>• Prospect engagement scoring</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-blue-900 mb-3">Supported CRM Platforms</h3>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-blue-800">
                <span className="bg-white px-4 py-2 rounded-md font-medium">Salesforce</span>
                <span className="bg-white px-4 py-2 rounded-md font-medium">HubSpot</span>
                <span className="bg-white px-4 py-2 rounded-md font-medium">Pipedrive</span>
                <span className="bg-white px-4 py-2 rounded-md font-medium">Close</span>
              </div>
            </div>

            <button
              onClick={() => {
                if (embedded) {
                  // If embedded in admin, switch to settings > integrations tab
                  window.dispatchEvent(new CustomEvent('navigate-to-settings', {
                    detail: { subTab: 'integrations' }
                  }))
                } else {
                  // If standalone page, navigate to admin settings integrations
                  window.location.href = '/admin#settings-integrations'
                }
              }}
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-lg transition"
            >
              <Settings className="h-5 w-5 mr-2" />
              Connect CRM in Settings
              <ArrowRight className="h-5 w-5 ml-2" />
            </button>
          </div>
        </div>
      </div>
    )

    if (embedded) {
      return setupContent
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-primary-600" />
                <h1 className="ml-3 text-2xl font-bold text-gray-900">Revenue Attribution</h1>
              </div>
              <a href="/admin" className="text-primary-600 hover:text-primary-700">← Back</a>
            </div>
          </div>
        </header>
        {setupContent}
      </div>
    )
  }

  // This code will be used when CRM is actually connected
  // SECURITY FIX: Add null checks to prevent crashes when CRM is connected but data is null
  const influencePercentage = data ? Math.round((data.trustCenterInfluenced / data.totalRevenue) * 100) : 0

  // Safety check: If CRM is connected but data is null, show error state
  if (!data) {
    return (
      <div className={embedded ? "p-8" : "min-h-screen bg-gray-50"}>
        <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-yellow-800">
              CRM is connected but no revenue data is available. Please check your CRM integration settings.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const content = (
    <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <DollarSign className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              ${(data.totalRevenue / 1000).toFixed(0)}K
            </p>
            <p className="text-sm text-gray-500 mt-1">Last 90 days</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Trust Center Influenced</p>
              <Target className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">
              ${(data.trustCenterInfluenced / 1000).toFixed(0)}K
            </p>
            <p className="text-sm text-green-600 mt-1">{influencePercentage}% of total</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{data.conversionRate}%</p>
            <p className="text-sm text-gray-500 mt-1">Leads to customers</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Avg Deal Size</p>
              <DollarSign className="h-5 w-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              ${(data.avgDealSize / 1000).toFixed(0)}K
            </p>
            <p className="text-sm text-gray-500 mt-1">Per customer</p>
          </div>
        </div>

        {/* Impact Explanation */}
        <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Trust Center ROI</h3>
          <p className="text-gray-700">
            Your trust center influenced <span className="font-bold text-green-600">${(data.trustCenterInfluenced / 1000).toFixed(0)}K</span> in
            revenue by accelerating security reviews and building buyer confidence. Prospects who downloaded
            compliance documents converted at <span className="font-bold">{data.conversionRate}%</span> vs industry average of 15%.
          </p>
        </div>

        {/* Top Performing Documents */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 text-primary-600 mr-2" />
              Top Revenue-Driving Documents
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {data.topDocuments.map((doc, index) => (
              <div key={index} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{doc.name}</h3>
                    <p className="text-sm text-gray-600">
                      {doc.downloads} downloads • {doc.dealsInfluenced} deals influenced
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">
                      ${(doc.revenue / 1000).toFixed(0)}K
                    </p>
                    <p className="text-sm text-gray-500">Revenue attributed</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Users className="h-4 w-4 mr-1" />
                    {doc.downloads} prospects
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Target className="h-4 w-4 mr-1" />
                    {Math.round((doc.dealsInfluenced / doc.downloads) * 100)}% conversion
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <TrendingUp className="h-6 w-6 text-primary-600 mr-2" />
              Monthly Performance
            </h2>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trust Center Leads</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Closed Deals</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.timeline.map((period, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{period.month}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{period.trustCenterLeads}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{period.closedDeals}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">
                        ${(period.revenue / 1000).toFixed(0)}K
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {Math.round((period.closedDeals / period.trustCenterLeads) * 100)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  )

  if (embedded) {
    return content
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Revenue Attribution</h1>
            </div>
            <a href="/admin" className="text-primary-600 hover:text-primary-700">← Back</a>
          </div>
        </div>
      </header>

      {content}
    </div>
  )
}

export default RevenueAttributionPage
